'use client';

import { useEffect } from 'react';

import type { RaffleOffersResponse } from '@/actions/raffleOffers';
import { usePromotionOffers, type PromotionOffersState } from '@/hooks';
import { toast } from '@/libs';
import type { HighlightedRaffleDetails } from '@/types/models';
import { normalizePackOffers, type PromotionOffer } from '@/utils';

import { HighlightedRaffleLayout } from './components';
import { useHighlightedRaffle } from './hooks';

type HighlightedRaffleProps = {
  raffle: HighlightedRaffleDetails;
  className?: string;
  initialOffers?: RaffleOffersResponse;
};

const OFFERS_FETCH_ERROR_MESSAGE =
  'Não foi possível atualizar as ofertas deste sorteio agora.';

function derivePackOffers({
  offersState,
  legacyPacks,
}: {
  offersState: PromotionOffersState;
  legacyPacks: unknown;
}): PromotionOffer[] {
  if (offersState.status === 'unified') {
    return normalizePackOffers({
      promotionOffers: offersState.offers,
      legacyPacks: undefined,
    });
  }

  if (offersState.status === 'legacy') {
    return normalizePackOffers({ promotionOffers: undefined, legacyPacks });
  }

  return [];
}

export const HighlightedRaffle = ({
  raffle,
  className,
  initialOffers,
}: HighlightedRaffleProps) => {
  const offersState = usePromotionOffers({
    raffleId: raffle.id,
    initialData: initialOffers,
  });
  const packOffers = derivePackOffers({
    offersState,
    legacyPacks: raffle.promotionPacks,
  });
  const promotionOffers =
    offersState.status === 'unified' ? offersState.offers : undefined;
  const promotionOffersResolved =
    offersState.status === 'unified' || offersState.status === 'legacy';

  useEffect(() => {
    if (offersState.status !== 'error') return;

    toast.error(OFFERS_FETCH_ERROR_MESSAGE);
  }, [offersState.status]);

  const {
    ticketQuantity,
    totalValue,
    addQuantity,
    handleComboClick,
    handleChangeQuantity,
    goToCheckout,
    hasQuantityError,
  } = useHighlightedRaffle({
    raffle,
    packOffers,
    promotionOffers,
    promotionOffersResolved,
  });

  return (
    <div className={className}>
      <HighlightedRaffleLayout
        raffle={raffle}
        packOffers={packOffers}
        ticketQuantity={ticketQuantity}
        totalValue={totalValue}
        addQuantity={addQuantity}
        handleComboClick={handleComboClick}
        handleChangeQuantity={handleChangeQuantity}
        goToCheckout={goToCheckout}
        hasQuantityError={hasQuantityError}
      />
    </div>
  );
};
